import config from '../config';

export enum AssetFileType {
    mothership = 'mothership',
    satellite = 'satellite',
    rmb = 'rmb',
    unity_rmb_actor = 'unity_rmb_actor.zip',
    unity_lambda_actor = 'unity_lambda_actor.zip',
    unknown = 'unknown',
}

export class BuildData {
    buildId: string = '';
    description: string = '';
    createDate: number = 0;
    gameId: string = '';
    fileTypes: AssetFileType[] = [];
    inUsed: boolean = false;
}

export const getDepotList = async (license: string, token?: string): Promise<BuildData[]> => {
    const resp = await fetch(`${config.entityManagerURL}/game/${license}/builds`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (resp.status !== 200) {
        throw new Error('failed to get depots');
    }
    const data = await resp.json();
    // depots are order by create_date, we want latest build first
    return data.data.builds.reverse();
};

export const downloadAsset = async (
    fileType: AssetFileType,
    buildData: BuildData,
    signal: AbortSignal,
    token?: string,
) => {
    const resp = await fetch(
        `${config.depotServiceURL}/game/${buildData.gameId}/depot/${buildData.buildId}/file/${fileType}`,
        {
            signal: signal,
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    );
    const data = await resp.json();
    window.open(data.data.presign, '_blank');
};

export const deleteDepot = async (buildData: BuildData, token?: string) => {
    const entityResp = await fetch(
        `${config.entityManagerURL}/game/${buildData.gameId}/builds/${buildData.buildId}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: 'DELETE',
        },
    );
    if (entityResp.status !== 200) {
        throw new Error('failed to delete depot');
    }

    const depotResp = await fetch(
        `${config.depotServiceURL}/game/${buildData.gameId}/depot/${buildData.buildId}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: 'DELETE',
        },
    );
    if (depotResp.status !== 200) {
        throw new Error('failed to delete depot');
    }
};

const getUploadPreSignedLinks = async (
    license: string,
    buildId: string,
    fileTypes: string[],
    token?: string,
) => {
    const reqs = fileTypes.map((fileType) => {
        return fetch(
            `${config.depotServiceURL}/game/${license}/depot/${buildId}/file/${fileType}`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );
    });
    return await Promise.allSettled(reqs);
};

const createBuildData = async (
    license: string,
    desc: string,
    fileTypes: string[],
    token?: string,
): Promise<BuildData | undefined> => {
    const result = await fetch(`${config.entityManagerURL}/game/${license}/builds`, {
        method: 'POST',
        body: JSON.stringify({
            description: desc,
            file_types: fileTypes,
        }),
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    if (result.ok) {
        const data = await result.json();
        return data.data;
    } else {
        return undefined;
    }
};

export const uploadToS3 = async (
    license: string,
    desc: string,
    fileTypes: any,
    files: any,
    token?: string,
): Promise<BuildData | undefined> => {
    const buildData = await createBuildData(license, desc, fileTypes, token);
    if (!buildData) {
        throw new Error('Request to create build failed');
    }

    const presignResps = await getUploadPreSignedLinks(
        license,
        buildData.buildId,
        fileTypes,
        token,
    );
    if (presignResps.some((resp) => resp.status === 'rejected' || resp.value.status !== 200)) {
        throw new Error('Request to create upload URL failed');
    }

    const uploadResps = await Promise.allSettled(
        presignResps.map(async (resp, i) => {
            if (resp.status === 'rejected') {
                // should never happen
                return null;
            }
            const resultJson = await resp.value.json();
            const uploadParams = resultJson.data.presign.fields;
            const uploadUrl = resultJson.data.presign.url;

            const presignedFormData = new FormData();
            for (const key of Object.keys(uploadParams)) {
                presignedFormData.set(key, uploadParams[key]);
            }
            // file must be the last parameter in the form, thus necessitating creating a new FormData object with "file" as the last parameter
            // https://docs.aws.amazon.com/AmazonS3/latest/API/sigv4-HTTPPOSTForms.html
            presignedFormData.set('file', files[i] as any);

            return fetch(`${uploadUrl}`, {
                method: 'POST',
                body: presignedFormData,
            });
        }),
    );

    if (
        uploadResps.some(
            (resp) => resp.status === 'rejected' || resp.value == null || resp.value.status >= 400,
        )
    ) {
        throw new Error('Upload failed');
    }

    return buildData;
};
